import { inject, Injectable, NgZone } from "@angular/core";
import { HttpClient, HttpEvent } from "@angular/common/http";
import { environment } from "../../../../../../environments/environment";
import { Observable, Subscriber } from "rxjs";
import { DownloadService } from "@kernel/tools/download-files/services/download.service";

/**
 * A service that provides utility functions for file management including:
 * - Uploading files
 * - Downloading files by key
 * - Programmatically opening a file picker dialog
 */
@Injectable({ providedIn: "root" })
export class FileService {
  private http = inject(HttpClient);
  private ngZone = inject(NgZone);
  private downloadService = inject(DownloadService);

  /**
   * Initiates a file download using the provided key.
   *
   * @param key - The identifier of the file to download.
   * @param saveFile - boolean value to save file. Defaults to `true`
   * @returns An Observable of the HTTP event stream for the file download (e.g., Blob data).
   */
  downloadFile(key: string, saveFile = true): Observable<HttpEvent<Blob>> {
    return this.downloadService.downloadFile(key, saveFile);
  }

  /**
   * Uploads one or more files using a multipart/form-data POST request.
   *
   * @param files - An array of `File` objects to be uploaded.
   * @returns An Observable of HTTP events, including progress and response.
   */
  uploadFile(files: (File | Blob)[]): Observable<any> {
    const formData = new FormData();
    files.forEach(file => formData.append("file", file));
    return this.http.post(`${environment.url}/rest/upload`, formData, {
      reportProgress: true,
      observe: "events",
    });
  }

  /**
   * Opens the native file picker dialog for selecting a single file.
   *
   * @param accept - Optional accepted file types (e.g., ".csv,.txt" or "image/*").
   * @param multiple - Should be `false` for single file selection.
   * @returns An Observable that emits the selected File or `null` if cancelled.
   */
  openFilesPicker(accept?: string | null, multiple?: false): Observable<File | null>;

  /**
   * Opens the native file picker dialog for selecting multiple files.
   *
   * @param accept - Optional accepted file types (e.g., ".csv,.txt" or "image/*").
   * @param multiple - Should be `true` for multiple file selection.
   * @returns An Observable that emits the selected FileList or `null` if cancelled.
   */
  openFilesPicker(accept?: string | null, multiple?: true): Observable<FileList | null>;

  /**
   * Opens the native file picker dialog, returning selected file(s) based on the `multiple` flag.
   *
   * This method runs outside Angular’s zone to avoid triggering change detection.
   * It listens for the `change` event to detect file selection, and uses the `focus` event
   * as a fallback to detect when the dialog is closed without selecting a file.
   *
   * @param accept - Optional string of accepted file types (e.g., ".csv,.txt" or "image/*").
   * @param multiple - Whether to allow selection of multiple files. Defaults to `false`.
   * @returns An Observable that emits a `File` (if `multiple` is false), a `FileList` (if true), or `null` if canceled.
   */
  openFilesPicker(accept?: string | null, multiple = false): Observable<File | FileList | null> {
    const subscribeHandler = (subscriber: Subscriber<File | FileList | null>) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = accept ?? "";
      input.multiple = multiple;
      input.hidden = true;

      const cleanup = () => {
        input.remove();
        window.removeEventListener("focus", windowFocusHandler);
      };

      const changeHandler = () => {
        if (!subscriber.closed) {
          const files = input.files;
          const result = files && (multiple ? files : files[0]);
          subscriber.next(result);
          subscriber.complete();
          cleanup();
        }
      };

      const windowFocusHandler = () => {
        setTimeout(() => {
          if (!subscriber.closed) {
            subscriber.next(null);
            subscriber.complete();
            cleanup();
          }
        }, 1000); // Delay ensures change event has a chance to fire first
      };

      window.addEventListener("focus", windowFocusHandler);
      input.addEventListener("change", changeHandler);
      document.body.appendChild(input);
      requestAnimationFrame(() => input.click());

      return cleanup;
    };

    return new Observable(subscriber => {
      return this.ngZone.runOutsideAngular(() => subscribeHandler(subscriber));
    });
  }
}
